import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check as CheckIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantAppearance } from '../../contexts/TenantAppearanceContext';
import configuracionService from '../../services/configuracionService';
import { TENANT_ACCENT_PALETTE } from '../../utils/tenantAccentColor';
import {
  APARIENCIA_ETIQUETA_MAX,
  APPEARANCE_PRESETS,
  AVISO_OPACIDAD_MAX,
  AVISO_OPACIDAD_MIN,
  AVISO_POSICION_OPCIONES,
  EMPTY_TENANT_APPEARANCE,
  accentInk,
  contrastTextColor,
  normalizeAppearance,
  normalizeHexColor,
  sameAppearance,
  type TenantAppearance,
} from '../../utils/tenantAppearance';
import TenantAvisoEntornoBanner from '../../components/shared/TenantAvisoEntornoBanner';
import { getApiErrorMessage } from '../../utils/apiError';

const SWATCH_SIZE = 32;

const AparienciaEmpresa: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { tenantId, tenantName, tenants } = useAuth();
  const { configuredAccents, applyAppearance } = useTenantAppearance();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guardada, setGuardada] = useState<TenantAppearance>(EMPTY_TENANT_APPEARANCE);
  const [form, setForm] = useState<TenantAppearance>(EMPTY_TENANT_APPEARANCE);
  const [hexInput, setHexInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    configuracionService
      .getApariencia()
      .then((actual) => {
        if (cancelled) return;
        setGuardada(actual);
        setForm(actual);
        setHexInput(actual.colorAcento ?? '');
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar la apariencia de la empresa'), {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, enqueueSnackbar]);

  const setColor = (color: string | null) => {
    setForm((prev) => ({ ...prev, colorAcento: color }));
    setHexInput(color ?? '');
  };

  const handleHexInput = (value: string) => {
    setHexInput(value);
    const normalized = normalizeHexColor(value);
    if (normalized || !value.trim()) {
      setForm((prev) => ({ ...prev, colorAcento: normalized }));
    }
  };

  const aplicarPreset = (appearance: TenantAppearance) => {
    setForm(appearance);
    setHexInput(appearance.colorAcento ?? '');
  };

  const colorRepetidoEn = useMemo(() => {
    if (!form.colorAcento) return [];
    return tenants.filter(
      (t) => t.id !== tenantId && configuredAccents[t.id]?.toLowerCase() === form.colorAcento,
    );
  }, [form.colorAcento, tenants, tenantId, configuredAccents]);

  const hexInvalido = hexInput.trim() !== '' && !normalizeHexColor(hexInput);
  const sinCambios = sameAppearance(normalizeAppearance(form), guardada);

  const handleGuardar = async () => {
    try {
      setSaving(true);
      const saved = await configuracionService.saveApariencia(form);
      setGuardada(saved);
      setForm(saved);
      applyAppearance(saved);
      enqueueSnackbar('Apariencia de la empresa actualizada', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar la apariencia'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const previewColor = form.colorAcento;
  const previewEtiqueta = form.etiqueta?.trim() || null;
  const previewInk = previewColor ? accentInk(previewColor) : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: 900, mx: 'auto' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
            Apariencia de la empresa
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Color, etiqueta y aviso con los que todos los usuarios ven{' '}
            <strong>{tenantName ?? 'la empresa activa'}</strong>. Sirve para distinguir de un vistazo en qué
            empresa o entorno se está trabajando.
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Configuración rápida
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {APPEARANCE_PRESETS.map((preset) => {
                  const activo = sameAppearance(normalizeAppearance(form), preset.appearance);
                  const color = preset.appearance.colorAcento ?? '#00548f';
                  return (
                    <ButtonBase
                      key={preset.id}
                      onClick={() => aplicarPreset(preset.appearance)}
                      sx={{
                        flex: 1,
                        display: 'block',
                        textAlign: 'left',
                        p: 1.5,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: activo ? color : alpha('#000', 0.12),
                        bgcolor: activo ? alpha(color, 0.08) : '#fff',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color }} />
                        <Typography fontWeight={700}>{preset.label}</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {preset.description}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Color
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Tooltip title="Sin color (estándar)">
                  <ButtonBase
                    onClick={() => setColor(null)}
                    aria-label="Sin color"
                    sx={{
                      width: SWATCH_SIZE,
                      height: SWATCH_SIZE,
                      borderRadius: '50%',
                      border: '2px dashed',
                      borderColor: form.colorAcento ? alpha('#000', 0.3) : '#111827',
                      bgcolor: '#fff',
                    }}
                  >
                    {!form.colorAcento && <CheckIcon fontSize="small" />}
                  </ButtonBase>
                </Tooltip>
                {TENANT_ACCENT_PALETTE.map((color) => {
                  const selected = form.colorAcento === color;
                  return (
                    <ButtonBase
                      key={color}
                      onClick={() => setColor(color)}
                      aria-label={`Color ${color}`}
                      sx={{
                        width: SWATCH_SIZE,
                        height: SWATCH_SIZE,
                        borderRadius: '50%',
                        bgcolor: color,
                        color: contrastTextColor(color),
                        outline: selected ? `3px solid ${alpha(color, 0.4)}` : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      {selected && <CheckIcon fontSize="small" />}
                    </ButtonBase>
                  );
                })}
                <Box
                  component="input"
                  type="color"
                  aria-label="Elegir otro color"
                  value={form.colorAcento ?? '#00548f'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColor(normalizeHexColor(e.target.value))}
                  sx={{
                    width: SWATCH_SIZE + 8,
                    height: SWATCH_SIZE + 8,
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  size="small"
                  label="Hex"
                  placeholder="#RRGGBB"
                  value={hexInput}
                  onChange={(e) => handleHexInput(e.target.value)}
                  error={hexInvalido}
                  helperText={hexInvalido ? 'Formato #RRGGBB' : ' '}
                  sx={{ width: 140 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Stack>
              {colorRepetidoEn.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Este color ya lo usa {colorRepetidoEn.map((t) => t.name).join(', ')}. Elegí uno distinto para
                  poder diferenciarlas.
                </Alert>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Etiqueta"
                placeholder="Ej.: PRINCIPAL, SECUNDARIO"
                value={form.etiqueta ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, etiqueta: e.target.value }))}
                inputProps={{ maxLength: APARIENCIA_ETIQUETA_MAX }}
                helperText={`Se muestra junto al nombre de la empresa y en el título de la pestaña (${(form.etiqueta ?? '').length}/${APARIENCIA_ETIQUETA_MAX}).`}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.mostrarAviso}
                    onChange={(e) => setForm((prev) => ({ ...prev, mostrarAviso: e.target.checked }))}
                  />
                }
                label="Mostrar aviso de entorno"
              />
            </Stack>

            {form.mostrarAviso && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Posición
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    La franja superior queda bajo el encabezado y la inferior, al pie de la pantalla. Las cintas
                    diagonales quedan en un vértice.
                  </Typography>
                  <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                    {AVISO_POSICION_OPCIONES.map((opcion) => {
                      const activa = form.avisoPosicion === opcion.id;
                      return (
                        <Tooltip key={opcion.id} title={opcion.descripcion}>
                          <Button
                            size="small"
                            variant={activa ? 'contained' : 'outlined'}
                            onClick={() => setForm((prev) => ({ ...prev, avisoPosicion: opcion.id }))}
                            sx={{ textTransform: 'none' }}
                          >
                            {opcion.label}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Opacidad
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    100% es sólida. Bajala para dejar ver la pantalla detrás de la cinta.
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Slider
                      value={form.avisoOpacidad}
                      min={AVISO_OPACIDAD_MIN}
                      max={AVISO_OPACIDAD_MAX}
                      step={5}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                      onChange={(_event, value) => {
                        setForm((prev) => ({ ...prev, avisoOpacidad: value as number }));
                      }}
                      aria-label="Opacidad del aviso"
                      sx={{ maxWidth: 360 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0 }}>
                      {form.avisoOpacidad}% opaca
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Vista previa
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha('#000', 0.12)}`,
                  minHeight:
                    form.mostrarAviso && form.avisoPosicion !== 'franja' ? 200 : undefined,
                  bgcolor: '#f3f4f6',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    height: 56,
                    bgcolor: previewColor ? `color-mix(in srgb, ${previewColor} 15%, #ffffff)` : '#fff',
                    borderLeft: previewInk ? `6px solid ${previewInk}` : undefined,
                  }}
                >
                  <Box component="img" src="/assets/eone-logo.png" alt="eOne" sx={{ height: 32 }} />
                  <Box sx={{ flex: 1 }} />
                  {previewEtiqueta && (
                    <Chip
                      label={previewEtiqueta}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: previewColor ?? '#4b5563',
                        color: contrastTextColor(previewColor ?? '#4b5563'),
                        border: previewInk ? `1px solid ${previewInk}` : undefined,
                      }}
                    />
                  )}
                  <Chip
                    label={tenantName ?? 'Empresa'}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: previewInk ?? undefined, color: previewInk ?? undefined }}
                  />
                </Box>
                {form.mostrarAviso && form.avisoPosicion === 'franja' && (
                  <TenantAvisoEntornoBanner
                    appearance={form}
                    tenantName={tenantName}
                    visible
                    preview
                  />
                )}
                <Box sx={{ px: 2, py: 2, pb: form.avisoPosicion === 'franja-inf' ? 6 : 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Contenido de la pantalla
                  </Typography>
                </Box>
                {form.mostrarAviso && form.avisoPosicion !== 'franja' && (
                  <TenantAvisoEntornoBanner
                    appearance={form}
                    tenantName={tenantName}
                    visible
                    preview
                  />
                )}
              </Box>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => aplicarPreset(guardada)}
                disabled={saving || sinCambios}
              >
                Descartar cambios
              </Button>
              <Button variant="contained" onClick={handleGuardar} disabled={saving || sinCambios || hexInvalido}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AparienciaEmpresa;
